import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Field, Input } from '@ui/components/ui';
import { AuthLayout, ErrorBox } from '@ui/auth/AuthPages';
import { useAcceptInviteMutation, useRegisterMutation } from '@ui/store/authApi';

/** Customer-only sign-up and invitation screens (the admin panel has neither). */

export function RegisterPage() {
  const [register, { isLoading, error }] = useRegisterMutation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', companyName: '', industry: '' });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await register({ ...form, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }).unwrap().catch(() => null);
    if (ok) navigate('/');
  };
  return (
    <AuthLayout title="Create your account" subtitle="Set up your workspace in under a minute." footer={<>Already have an account? <Link to="/login" className="link">Sign in</Link></>}>
      <form onSubmit={submit} className="space-y-4">
        <ErrorBox error={error} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name"><Input value={form.firstName} onChange={set('firstName')} required /></Field>
          <Field label="Last name"><Input value={form.lastName} onChange={set('lastName')} /></Field>
        </div>
        <Field label="Company"><Input value={form.companyName} onChange={set('companyName')} required /></Field>
        <Field label="Industry" hint="e.g. Real estate, Healthcare, Restaurants"><Input value={form.industry} onChange={set('industry')} /></Field>
        <Field label="Work email"><Input type="email" value={form.email} onChange={set('email')} required /></Field>
        <Field label="Password" hint="At least 10 characters"><Input type="password" value={form.password} onChange={set('password')} minLength={10} required /></Field>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={isLoading}>
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [accept, { isLoading, error }] = useAcceptInviteMutation();
  const [form, setForm] = useState({ firstName: '', lastName: '', password: '' });
  return (
    <AuthLayout title="Join your team" subtitle="Set your name and a password to accept the invitation.">
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const ok = await accept({ token: params.get('token') ?? '', ...form }).unwrap().catch(() => null);
          if (ok) navigate('/');
        }}
        className="space-y-4"
      >
        <ErrorBox error={error} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name"><Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required /></Field>
          <Field label="Last name"><Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></Field>
        </div>
        <Field label="Password" hint="At least 10 characters"><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} minLength={10} required /></Field>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={isLoading}>Accept invitation</Button>
      </form>
    </AuthLayout>
  );
}
